/**
 * EOD V2 — reports page (2026-02 rebuild). Serves BOTH:
 *   "My EOD" (/eod) — every user, self scope
 *   "Team EOD Report" (/tasks/eod-report) — owner/admin/manager scope
 * Scope is enforced server-side (/eod-v2/reports); this page just renders
 * whatever the API allows and shows the person filter when permitted.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Navigate, useLocation } from 'react-router-dom';
import { ClipboardList, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { eodV2API } from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { StellarDateRangePicker } from '../components/StellarDateRangePicker';
import { localDateStr, fmtDateLong, fmtTime } from '../lib/attendanceV2';

function presetRange(preset) {
  const now = new Date();
  const today = localDateStr(now);
  if (preset === 'today') return { start: today, end: today };
  if (preset === 'week') {
    const mon = new Date(now);
    mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(sun.getDate() + 6);
    return { start: localDateStr(mon), end: localDateStr(sun) };
  }
  if (preset === 'month') {
    return {
      start: localDateStr(new Date(now.getFullYear(), now.getMonth(), 1)),
      end: localDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  return null;
}

export default function EodReportsPage() {
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const { user } = useAuth();
  const location = useLocation();
  const wsId = currentWorkspace?.id;
  // 2026-02 — Owner uses ONLY Team EOD Report; /eod redirects there.
  const isOwner = user?.role === 'super_admin' || workspaceRole === 'owner';

  const [preset, setPreset] = useState('week');
  const [range, setRange] = useState(presetRange('week'));
  const [person, setPerson] = useState('all');
  const [members, setMembers] = useState([]);
  const [reports, setReports] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [canViewTeam, setCanViewTeam] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const res = await eodV2API.reports(wsId, range.start, range.end, person === 'all' ? '' : person);
      setReports(res.data.reports || []);
      const team = !!res.data.can_view_team;
      setCanViewTeam(team);
      if (team) {
        // Owner/admin/manager: surface days where someone clocked out
        // without submitting an EOD (mainly pre-V2 history). Read-only.
        eodV2API.gaps(wsId, range.start, range.end)
          .then((g) => setGaps(g.data.gaps || []))
          .catch(() => setGaps([]));
        if (members.length === 0) {
          eodV2API.members(wsId).then((r) => setMembers(r.data.members || [])).catch(() => {});
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load EOD reports.');
    } finally {
      setLoading(false);
    }
  }, [wsId, range, person]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => {
    setPreset(p);
    const r = presetRange(p);
    if (r) setRange(r);
  };

  if (isOwner && location.pathname === '/eod') {
    return <Navigate to="/tasks/eod-report" replace />;
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl" data-testid="eod-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-gold" /> EOD Reports
          </h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            {canViewTeam ? 'Daily work summaries, tied to attendance.' : 'My EOD history only.'}
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          {canViewTeam && (
            <Select value={person} onValueChange={setPerson}>
              <SelectTrigger className="h-8 w-[170px] text-[12px]" data-testid="eod-person-filter">
                <SelectValue placeholder="Everyone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="h-8 w-[130px] text-[12px]" data-testid="eod-range-preset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="month">This month</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
          <StellarDateRangePicker
            value={range}
            onChange={(r) => { setRange(r); setPreset('custom'); }}
            testId="eod-range-picker"
          />
        </div>
      </div>

      <div className="space-y-2">
        {!loading && canViewTeam && gaps.length > 0 && (
          <div className="flex flex-wrap gap-1.5 items-center" data-testid="eod-gaps-strip">
            {gaps.map((g) => (
              <span
                key={`${g.user_id}-${g.date}`}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/25"
                title={`${g.user_name} clocked out on ${fmtDateLong(g.date)} without submitting an EOD (historical)`}
                data-testid={`eod-gap-${g.user_id}-${g.date}`}
              >
                ⚠ {g.user_name} · {fmtDateLong(g.date)} — no EOD submitted
              </span>
            ))}
          </div>
        )}
        {loading && <p className="text-[12px] text-muted-foreground py-6 text-center">Loading…</p>}
        {!loading && reports.length === 0 && (
          <p className="text-[12px] text-muted-foreground py-6 text-center" data-testid="eod-empty">
            No EOD reports in this range yet. Reports are created when someone clocks out.
          </p>
        )}
        {!loading && reports.map((r) => {
          const isOpen = expanded === r.id;
          return (
            <div key={r.id} className="border border-border/40 rounded-lg" data-testid={`eod-report-${r.id}`}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : r.id)}
                className="w-full flex items-center gap-2.5 px-3.5 py-3 text-left hover:bg-foreground/[0.03] rounded-lg"
                data-testid={`eod-report-toggle-${r.id}`}
              >
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium">{r.user_name}</span>
                    <span className="text-[11px] text-muted-foreground">{fmtDateLong(r.date)}</span>
                    <span className="text-[10px] text-muted-foreground/60">submitted {fmtTime(r.submitted_at)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {(r.tasks || []).length
                      ? (r.tasks || []).map((t) => t.title).join(' · ')
                      : 'No task activity — notes only'}
                  </p>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gold/10 text-gold shrink-0">
                  {(r.tasks || []).length} task{(r.tasks || []).length === 1 ? '' : 's'}
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-border/30 pt-3">
                  {r.summary ? (
                    <>
                      {/* 2026-07-14 short-summary model */}
                      <div className="space-y-0.5">
                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Today’s summary</span>
                        <p className="text-[12px] text-foreground/85 whitespace-pre-wrap" data-testid={`eod-report-summary-${r.id}`}>{r.summary}</p>
                      </div>
                      {r.blockers && (
                        <div className="space-y-0.5">
                          <span className="text-[11px] uppercase tracking-wide text-amber-500/80">Blockers</span>
                          <p className="text-[12px] text-muted-foreground whitespace-pre-wrap">{r.blockers}</p>
                        </div>
                      )}
                      {r.plan_tomorrow && (
                        <div className="space-y-0.5">
                          <span className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Plan for tomorrow</span>
                          <p className="text-[12px] text-muted-foreground whitespace-pre-wrap">{r.plan_tomorrow}</p>
                        </div>
                      )}
                      {(r.tasks || []).length > 0 && (
                        <div className="space-y-1">
                          <span className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Tasks touched ({(r.tasks || []).length})</span>
                          <div className="flex flex-wrap gap-1.5">
                            {(r.tasks || []).map((t) => (
                              <span key={t.task_id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-foreground/[0.05]">
                                {t.title}
                                {t.client_name && <span className="text-gold">· {t.client_name}</span>}
                                <span className="text-muted-foreground/60 uppercase">{(t.status || '').replace('_', ' ')}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    (r.tasks || []).map((t) => (
                      <div key={t.task_id} className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] font-medium">{t.title}</span>
                          {t.client_name && <span className="text-[9px] px-1 py-0.5 rounded bg-gold/10 text-gold">{t.client_name}</span>}
                          <span className="text-[9px] px-1 py-0.5 rounded bg-foreground/[0.05] text-muted-foreground uppercase">{(t.status || '').replace('_', ' ')}</span>
                        </div>
                        <p className="text-[12px] text-muted-foreground whitespace-pre-wrap">{t.description}</p>
                      </div>
                    ))
                  )}
                  {r.extra_notes && (
                    <div className="space-y-0.5">
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Extra notes</span>
                      <p className="text-[12px] text-muted-foreground whitespace-pre-wrap">{r.extra_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
